import * as React from "react";
import {cx} from "emotion";

import "./mail-options.scss";

import {bindInput}              from "@Common/react/bind-input";
import {cs}                     from "@Common/react/chain-services";
import {Form2}                  from "@Common/react/cs-form-2/form2";
import {isInteger}              from "@Common/react/cs-form-2/validators/is-integer";
import {isNumber}               from "@Common/react/cs-form-2/validators/is-number";
import {max}                    from "@Common/react/cs-form-2/validators/max";
import {min}                    from "@Common/react/cs-form-2/validators/min";
import {required}               from "@Common/react/cs-form-2/validators/required";
import {stateChangeNestedValue} from "@Common/react/state-change-nested-value";
import {chain}                  from "@Common/utils/fs";

import {formatMoney}      from "../../../../../../../language/format-money";
import {formatNumberBig}  from "../../../../../../../language/format-number-big";
import {ErrorHighlighter} from "../../../../../../../light/error-highlighter";
import {RadioOption}      from "../../../common/radio-option/radio-option";
import {getErrorMessage}  from "../search-form/common/get-error-message";

export const MailOptions = ({state, total, query, next}) => cs(
    ["limitInputEH", (_, next) => ErrorHighlighter({next})],
    ["mailOptionEH", (_, next) => ErrorHighlighter({next})],
    ["useTypeEH", (_, next) => ErrorHighlighter({next})],

    ["form", ({}, next) => Form2({
        data: state,
        showErrors: true,
        fields: {
            mailCount: [
                required,
                isNumber,
                isInteger,
                min(100),
                max(total ?? 100000),
            ],
        },
        next,
    })],

    ({limitInputEH, mailOptionEH, useTypeEH, form}) => next({
        invalid: !state.value?.mailOption || (
            state.value.mailOption === "limit" && form.field("mailCount").error
        ) || !state.value?.useType,
        highlightErrors: () => {
            if (!state.value?.mailOption) {
                mailOptionEH.start();
            } else if (state.value.mailOption === "limit" && form.field("mailCount").error) {
                limitInputEH.start();
            } else if (!state.value?.useType) {
                useTypeEH.start();
            }
        },
        render: ({pricePerPiece, showErrors, showCountOption}) => (
            <div className="mail-options-2h0">
                <div className="search-results" >
                    <div className="title" xd-qa-type="label" xd-qa-name="Demographics Search Current Count">
                        Current Count: <span xd-qa-type="label" xd-qa-name="Demographics Search Current Count Amount">{formatNumberBig(total)}</span>
                    </div>
                    <div className="subtitle" xd-qa-type="label" xd-qa-name="Demographics Search Result">
                        <span xd-qa-type="label" xd-qa-name="Demographics Search Result Data">{querySummary(query)}</span>
                    </div>
                </div>
                {showCountOption && 
                    <div className="count-option">
                        <div className="left">

                        </div>
                        <div className={cx("right", mailOptionEH.css)}>
                            {(() => {
                                const Option = ({value, label, getExtra=undefined}) => {
                                    const selected = state.value?.["mailOption"] === value;

                                    return RadioOption({
                                        label,
                                        selected,
                                        extra: selected && getExtra?.(),

                                        onSelect: () => state.change((s) => ({
                                            ...s,
                                            mailCount: null,
                                            mailOption: value,
                                        })),
                                        xdQaName: "Demographics Search "+label,
                                    });
                                };
                                return <>
                                    {Option({
                                        label: "Mail to All Addresses",
                                        value: "all",
                                    })}
                                    {Option({
                                        label: "Mail to a Custom Quantity",
                                        value: "limit",

                                        getExtra: () => chain(
                                            form.field("mailCount"),
                                            (field) => <>
                                                <input {...{
                                                    autoFocus: true,
                                                    placeholder: total,
                                                    maxLength: 50,
                                                    className: cx(limitInputEH.css, {hasError: showErrors && field.error}),
                                                    ...bindInput(field.state),
                                                    "xd-qa-type": "text_field",
                                                    "xd-qa-name": "Demographics Search Mail to a Custom Quantity",
                                                }}/>
                                                {showErrors && field.error && (
                                                    <div className="error" xd-qa-type="label" xd-qa-name="Demographics Search Mail to a Custom Quantity Error">
                                                        {getErrorMessage(field.error)}
                                                    </div>
                                                )}
                                            </>
                                        ),
                                    })}
                                </>;
                            })()}
                        </div>
                    </div>
                }

                <div className="reuse-option">
                    <div className="left">
                        <div className="title" xd-qa-type="label" xd-qa-name="Demographics Search How Often">
                            How often will you use this list?
                        </div>
                    </div>
                    <div className={cx("right", useTypeEH.css)}>
                        {(() => {
                            const Option = ({value, price, label}) => {
                                const key = "useType";
                                const selected = state.value?.[key] === value;
                                return RadioOption({
                                    label: <>{label} <span className="price-3ed" xd-qa-type="label" xd-qa-name={"Demographics Search "+label+" Price"}>({formatMoney(price * (state.value?.mailCount || total))})</span></>,
                                    selected,

                                    onSelect: () => stateChangeNestedValue(state, [key], () => value),
                                    xdQaName: "Demographics Search "+label,
                                });
                            };
                            return <>
                                {Option({
                                    label: "Single Use",
                                    price: pricePerPiece.singleUse,
                                    value: "single",
                                })}
                                {Option({
                                    label: "Multiple Use",
                                    price: pricePerPiece.multipleUse,
                                    value: "unlimited",
                                })}

                            </>;
                        })()}
                    </div>
                </div>
            </div>
        ),
    }),
);

const querySummary = (query) => {
    const summaryMax = 25

    if(!query) {
        return ""
    }

    if(!query.value?.searchType) {
        return ""
    }    

    let summary
    switch(query.value?.searchType) {           
        case "address_quantity":
            summary = `${query.value?.quantity} addresses near ${query.value?.address}`
            break
        case "postal_code":
            const zip = query.value?.postalCodes.split(',').length > 1 
                       ? "Zip codes"
                       : "Zip code"
            summary = `${zip} ${query.value?.postalCodes}`
            break
        case "address_distance":
        default:
            const mile = query.value?.distance > 1
                           ? "miles"
                           : "mile" 
            summary = `${query.value?.distance} ${mile} near ${query.value?.address}`
            break
    }

    summary = summary.length > summaryMax 
            ? `${summary.substring(0, summaryMax)}...`
            : summary

    return summary
}
